import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useDropzone } from "react-dropzone";
import SubPageLayout from "../../components/shared/SubPageLayout";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Tooltip } from "react-tooltip";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { isEqual } from "lodash";

import logger from '../../utils/logger';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
);

// Widget Components
const MetricWidget = ({ title, value, trend, status = "neutral", icon }) => {
  const getStatusColor = () => {
    switch (status) {
      case "success":
        return "text-green-500 dark:text-green-400";
      case "warning":
        return "text-yellow-500 dark:text-yellow-400";
      case "error":
        return "text-red-500 dark:text-red-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-none p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
        <span className={`${getStatusColor()}`}>{icon}</span>
      </div>
      <div className="flex items-baseline">
        <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
        {trend && (
          <span
            className={`ml-2 text-sm ${
              trend > 0 
                ? "text-green-500 dark:text-green-400" 
                : "text-red-500 dark:text-red-400"
            }`}
          >
            {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
};

const WorkflowProgress = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center">
            <button
              onClick={() => onStepClick(index)}
              className={`flex items-center justify-center w-8 h-8 rounded-full 
                ${
                  index <= currentStep
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500 dark:text-gray-500"
                }`}
              data-tooltip-id={`step-${index}`}
              data-tooltip-content={step.description}
            >
              {index + 1}
            </button>
            <Tooltip id={`step-${index}`} />
            {index < steps.length - 1 && (
              <div
                className={`h-1 w-16 mx-2 
                ${index < currentStep ? "bg-blue-600" : "bg-gray-200"}`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {steps[currentStep].title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-500">
          {steps[currentStep].description}
        </p>
      </div>
    </div>
  );
};

const UnsavedChangesWarning = ({ isVisible, onConfirm, onCancel }) => (
  <AnimatePresence>
    {isVisible && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      >
        <div className="bg-surface-default dark:bg-surface-default rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Unsaved Changes
          </h3>
          <p className="text-gray-500 dark:text-gray-500 mb-6">
            You have unsaved changes. Are you sure you want to leave this page?
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
            >
              Stay
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Leave
            </button>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Custom hooks for trade execution and market data
const useTradeExecution = ({
  selectedAccount,
  accountAllocations,
  marketData,
  setAccountAllocations,
  setMetrics,
}) => {
  const [orderStatus, setOrderStatus] = useState("idle");
  const [validationErrors, setValidationErrors] = useState([]);

  const validateOrder = useCallback(
    async (order) => {
      // Mock API call for trade validation
      try {
        // In real implementation, this would call the Schwab/iRebal validation API
        await new Promise((resolve) => setTimeout(resolve, 500));

        const selectedAccountData = accountAllocations.find(
          (acc) => acc.accountId === selectedAccount,
        );
        const estimatedValue =
          order.quantity * (marketData[order.security]?.lastPrice || 0);

        const errors = [];

        // Common validations
        if (!order.security) {
          errors.push("Security symbol is required");
        }
        if (!order.quantity || order.quantity <= 0) {
          errors.push("Quantity must be greater than 0");
        }

        // Buy order specific validations
        if (order.orderType === "buy") {
          if (estimatedValue > selectedAccountData.cash) {
            errors.push("Insufficient funds for this purchase");
          }
        }
        // Sell order specific validations
        else if (order.orderType === "sell") {
          const position = selectedAccountData.positions.find(
            (p) => p.symbol === order.security,
          );
          if (!position) {
            errors.push("Selected security not found in portfolio");
          } else if (order.quantity > position.quantity) {
            errors.push("Insufficient shares for this sale");
          }
        }

        const mockValidation = {
          isValid: errors.length === 0,
          errors,
          warnings: [],
          taxImplications:
            order.orderType === "sell"
              ? {
                  estimatedGain: Math.random() * 1000,
                  washSaleWarning: Math.random() > 0.8,
                  shortTermGainWarning: Math.random() > 0.7,
                }
              : null,
        };

        setValidationErrors(mockValidation.errors);
        return mockValidation;
      } catch (error) {
        logger.error("Trade validation failed:", error);
        setValidationErrors(["Failed to validate trade"]);
        return { isValid: false, errors: ["Validation failed"] };
      }
    },
    [selectedAccount, accountAllocations, marketData],
  );

  const executeOrder = useCallback(
    async (order) => {
      setOrderStatus("validating");
      const validation = await validateOrder(order);

      if (!validation.isValid) {
        setOrderStatus("error");
        return { success: false, errors: validation.errors };
      }

      try {
        setOrderStatus("executing");
        // Mock API call for trade execution
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update account positions and cash
        const selectedAccountData = accountAllocations.find(
          (acc) => acc.accountId === selectedAccount,
        );
        const estimatedValue =
          order.quantity * (marketData[order.security]?.lastPrice || 0);

        setAccountAllocations((prev) =>
          prev.map((account) => {
            if (account.accountId === selectedAccount) {
              let updatedPositions = [...account.positions];
              let updatedCash = account.cash;

              if (order.orderType === "buy") {
                updatedCash -= estimatedValue;
                const existingPosition = updatedPositions.find(
                  (p) => p.symbol === order.security,
                );
                if (existingPosition) {
                  updatedPositions = updatedPositions.map((p) =>
                    p.symbol === order.security
                      ? {
                          ...p,
                          quantity: p.quantity + parseInt(order.quantity),
                        }
                      : p,
                  );
                } else {
                  updatedPositions.push({
                    symbol: order.security,
                    quantity: parseInt(order.quantity),
                    value: estimatedValue,
                    allocation: (estimatedValue / account.totalValue) * 100,
                  });
                }
              } else {
                updatedCash += estimatedValue;
                updatedPositions = updatedPositions
                  .map((p) =>
                    p.symbol === order.security
                      ? {
                          ...p,
                          quantity: p.quantity - parseInt(order.quantity),
                        }
                      : p,
                  )
                  .filter((p) => p.quantity > 0);
              }

              return {
                ...account,
                cash: updatedCash,
                positions: updatedPositions,
                totalValue:
                  account.totalValue +
                  (order.orderType === "buy" ? 0 : estimatedValue),
              };
            }
            return account;
          }),
        );

        // Update dashboard metrics
        setMetrics((prev) => ({
          ...prev,
          pendingTrades: {
            ...prev.pendingTrades,
            value: prev.pendingTrades.value + 1,
          },
          lastExecutionTime: {
            ...prev.lastExecutionTime,
            value: new Date().toLocaleTimeString(),
          },
          tradeSuccessRate: {
            ...prev.tradeSuccessRate,
            value: "100%",
            status: "success",
          },
          cashSufficiency: {
            ...prev.cashSufficiency,
            value: `$${(selectedAccountData.cash + (order.orderType === "sell" ? estimatedValue : -estimatedValue)).toLocaleString()}`,
            trend: order.orderType === "sell" ? 5.2 : -5.2,
          },
        }));

        setOrderStatus("completed");
        return {
          success: true,
          confirmationNumber: Math.random()
            .toString(36)
            .substring(7)
            .toUpperCase(),
          executionTime: new Date().toISOString(),
          orderType: order.orderType,
          estimatedValue,
        };
      } catch (error) {
        setOrderStatus("error");
        return { success: false, errors: ["Trade execution failed"] };
      }
    },
    [
      selectedAccount,
      accountAllocations,
      marketData,
      validateOrder,
      setAccountAllocations,
      setMetrics,
    ],
  );

  return { orderStatus, validationErrors, validateOrder, executeOrder };
};

// Custom hook for market data
const useMarketData = (symbols) => {
  const [marketData, setMarketData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const baseMarketData = {
    AAPL: {
      lastPrice: 175.5,
      bidPrice: 175.35,
      askPrice: 175.65,
      volume: 1000000,
      priceChange: 0,
    },
    MSFT: {
      lastPrice: 325.75,
      bidPrice: 325.6,
      askPrice: 325.9,
      volume: 800000,
      priceChange: 0,
    },
    GOOGL: {
      lastPrice: 135.6,
      bidPrice: 135.45,
      askPrice: 135.75,
      volume: 600000,
      priceChange: 0,
    },
  };

  const generateMarketData = useCallback(() => {
    return symbols.reduce((acc, symbol) => {
      const baseData = baseMarketData[symbol];
      if (!baseData) return acc;

      // Generate small random variations (±0.5%) only when refreshing
      const variation = (Math.random() - 0.5) * 0.01 * baseData.lastPrice;
      const lastPrice = baseData.lastPrice + variation;

      acc[symbol] = {
        lastPrice,
        bidPrice: lastPrice - lastPrice * 0.001,
        askPrice: lastPrice + lastPrice * 0.001,
        volume: baseData.volume + Math.floor(Math.random() * 100000),
        timestamp: new Date().toISOString(),
        priceChange: variation,
      };
      return acc;
    }, {});
  }, [symbols]);

  // Function to manually refresh data
  const refreshMarketData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      const newData = generateMarketData();
      setMarketData(newData);
      setLastUpdate(new Date());
    } catch (error) {
      logger.error("Failed to fetch market data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [generateMarketData]);

  // Initialize with base market data
  useEffect(() => {
    if (symbols.length > 0) {
      // Use base values without variations for initial load
      const initialData = symbols.reduce((acc, symbol) => {
        if (baseMarketData[symbol]) {
          acc[symbol] = {
            ...baseMarketData[symbol],
            timestamp: new Date().toISOString(),
          };
        }
        return acc;
      }, {});
      setMarketData(initialData);
      setLastUpdate(new Date());
      setIsLoading(false);
    }
  }, [symbols]); // Only run on mount and when symbols change

  return { marketData, isLoading, lastUpdate, refreshMarketData };
};

// Custom hook for iRebal integration
const useIRebal = () => {
  const [rebalanceStatus, setRebalanceStatus] = useState("idle");
  const [rebalanceResults, setRebalanceResults] = useState(null);

  const analyzeRebalancing = useCallback(async (accounts, options) => {
    try {
      setRebalanceStatus("analyzing");
      // Mock API call for iRebal analysis
      await new Promise((resolve) => setTimeout(resolve, 800));

      const mockAnalysis = {
        proposedTrades: [
          {
            accountId: accounts[0],
            trades: [
              {
                symbol: "AAPL",
                action: "SELL",
                quantity: 50,
                estimatedValue: 8775,
              },
              {
                symbol: "MSFT",
                action: "BUY",
                quantity: 25,
                estimatedValue: 8143.75,
              },
            ],
          },
        ],
        taxImplications: {
          totalEstimatedGain: 2500,
          shortTermGains: 1200,
          longTermGains: 1300,
          washSaleWarnings: [],
        },
        cashProjections: {
          beforeRebalance: 15000,
          afterRebalance: 15631.25,
          pendingDistributions: 2500,
        },
      };

      setRebalanceResults(mockAnalysis);
      setRebalanceStatus("ready");
      return mockAnalysis;
    } catch (error) {
      logger.error("iRebal analysis failed:", error);
      setRebalanceStatus("error");
      return null;
    }
  }, []);

  const executeRebalancing = useCallback(async (analysis) => {
    try {
      setRebalanceStatus("executing");
      // Mock API call for iRebal execution
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const mockResult = {
        success: true,
        executedTrades: analysis.proposedTrades,
        confirmationNumber: Math.random()
          .toString(36)
          .substring(7)
          .toUpperCase(),
        executionTime: new Date().toISOString(),
      };

      setRebalanceStatus("completed");
      return mockResult;
    } catch (error) {
      logger.error("iRebal execution failed:", error);
      setRebalanceStatus("error");
      return { success: false, error: "Rebalancing execution failed" };
    }
  }, []);

  return {
    rebalanceStatus,
    rebalanceResults,
    analyzeRebalancing,
    executeRebalancing,
  };
};

// Mock data definitions
const INITIAL_POSITIONS = [
  {
    symbol: "AAPL",
    name: "Apple Inc.",
    quantity: 150,
    marketPrice: 175.5,
    value: 26325,
    gainLoss: 3750,
  },
  {
    symbol: "MSFT",
    name: "Microsoft Corp",
    quantity: 120,
    marketPrice: 325.75,
    value: 39090,
    gainLoss: 5940,
  },
  {
    symbol: "GOOGL",
    name: "Alphabet Inc.",
    quantity: 80,
    marketPrice: 135.6,
    value: 10848,
    gainLoss: 1424,
  },
  {
    symbol: "AMZN",
    name: "Amazon.com Inc.",
    quantity: 95,
    marketPrice: 145.25,
    value: 13799,
    gainLoss: 2100,
  },
  {
    symbol: "NVDA",
    name: "NVIDIA Corp",
    quantity: 60,
    marketPrice: 485.9,
    value: 29154,
    gainLoss: 4800,
  },
  {
    symbol: "META",
    name: "Meta Platforms Inc.",
    quantity: 110,
    marketPrice: 325.3,
    value: 35783,
    gainLoss: 5280,
  },
  {
    symbol: "BRK.B",
    name: "Berkshire Hathaway Inc.",
    quantity: 75,
    marketPrice: 355.45,
    value: 26659,
    gainLoss: 3150,
  },
  {
    symbol: "JPM",
    name: "JPMorgan Chase & Co.",
    quantity: 130,
    marketPrice: 165.75,
    value: 21548,
    gainLoss: 2860,
  },
  {
    symbol: "JNJ",
    name: "Johnson & Johnson",
    quantity: 90,
    marketPrice: 155.2,
    value: 13968,
    gainLoss: 1620,
  },
  {
    symbol: "V",
    name: "Visa Inc.",
    quantity: 100,
    marketPrice: 245.8,
    value: 24580,
    gainLoss: 3500,
  },
];

// Rebalancing dummy data
const INITIAL_ACCOUNT_ALLOCATIONS = [
  {
    accountId: "1234",
    accountName: "John Smith IRA",
    currentAllocation: 40,
    targetAllocation: 35,
    cash: 25000,
    positions: [
      {
        symbol: "AAPL",
        quantity: 85,
        marketPrice: 175.5,
        value: 14917.5,
        allocation: 15,
      },
      {
        symbol: "MSFT",
        quantity: 65,
        marketPrice: 325.75,
        value: 21173.75,
        allocation: 20,
      },
      {
        symbol: "GOOGL",
        quantity: 45,
        marketPrice: 135.6,
        value: 6102.0,
        allocation: 10,
      },
    ],
    totalValue: 67193.25,
    portfolioBreakdown: [
      {
        name: "RN 60",
        allocation: 50,
        riskScore: 60,
        ytdReturn: 3.8,
        oneYearReturn: 21.4,
      },
      {
        name: "Sector Rotating",
        allocation: 30,
        riskScore: 55,
        ytdReturn: 3.6,
        oneYearReturn: 20.2,
      },
      {
        name: "RN 45",
        allocation: 15,
        riskScore: 45,
        ytdReturn: 3.2,
        oneYearReturn: 18.7,
      },
      {
        name: "Cash",
        allocation: 5,
        riskScore: 1,
        ytdReturn: 0.5,
        oneYearReturn: 2.0,
      },
    ],
  },
  {
    accountId: "5678",
    accountName: "Sarah Johnson 401k",
    currentAllocation: 35,
    targetAllocation: 40,
    cash: 18500,
    positions: [
      {
        symbol: "NVDA",
        quantity: 35,
        marketPrice: 485.9,
        value: 17006.5,
        allocation: 20,
      },
      {
        symbol: "META",
        quantity: 55,
        marketPrice: 325.3,
        value: 17891.5,
        allocation: 25,
      },
      {
        symbol: "AMZN",
        quantity: 40,
        marketPrice: 145.25,
        value: 5810.0,
        allocation: 15,
      },
    ],
    totalValue: 59208.0,
    portfolioBreakdown: [
      {
        name: "RN 30",
        allocation: 40,
        riskScore: 30,
        ytdReturn: 2.9,
        oneYearReturn: 15.6,
      },
      {
        name: "Equity",
        allocation: 35,
        riskScore: 50,
        ytdReturn: 3.5,
        oneYearReturn: 19.8,
      },
      {
        name: "RN 15",
        allocation: 20,
        riskScore: 15,
        ytdReturn: 1.8,
        oneYearReturn: 12.4,
      },
      {
        name: "Cash",
        allocation: 5,
        riskScore: 1,
        ytdReturn: 0.5,
        oneYearReturn: 2.0,
      },
    ],
  },
  {
    accountId: "9012",
    accountName: "David Wilson Trust",
    currentAllocation: 25,
    targetAllocation: 25,
    cash: 22000,
    positions: [
      {
        symbol: "V",
        quantity: 50,
        marketPrice: 245.8,
        value: 12290.0,
        allocation: 25,
      },
      {
        symbol: "JPM",
        quantity: 70,
        marketPrice: 165.75,
        value: 11602.5,
        allocation: 30,
      },
      {
        symbol: "JNJ",
        quantity: 45,
        marketPrice: 155.2,
        value: 6984.0,
        allocation: 15,
      },
    ],
    totalValue: 52876.5,
    portfolioBreakdown: [
      {
        name: "NQ Bond",
        allocation: 35,
        riskScore: 25,
        ytdReturn: 0.9,
        oneYearReturn: 5.6,
      },
      {
        name: "NQ Equity",
        allocation: 30,
        riskScore: 65,
        ytdReturn: 4.2,
        oneYearReturn: 24.6,
      },
      {
        name: "Bond Alternative",
        allocation: 25,
        riskScore: 1,
        ytdReturn: 2.5,
        oneYearReturn: 8.2,
      },
      {
        name: "Cash",
        allocation: 10,
        riskScore: 1,
        ytdReturn: 0.5,
        oneYearReturn: 2.0,
      },
    ],
  },
];

const INITIAL_FIRMWIDE_METRICS = {
  totalPortfolioValue: 386500,
  totalCash: 35500,
  cashAllocation: 9.18,
  riskScore: 75,
  assetAllocation: {
    equity: 65.82,
    fixedIncome: 25.0,
    cash: 9.18,
  },
  sectorAllocation: {
    technology: 45.5,
    healthcare: 15.2,
    financials: 12.8,
    consumerDiscretionary: 10.5,
    other: 16.0,
  },
};

const INITIAL_DISTRIBUTIONS = [
  {
    accountId: "1234",
    accountName: "John Smith IRA",
    amount: 2500,
    frequency: "Monthly",
    nextDate: "2024-04-15",
  },
  {
    accountId: "5678",
    accountName: "Sarah Johnson 401k",
    amount: 1500,
    frequency: "Quarterly",
    nextDate: "2024-06-30",
  },
];

const INITIAL_CASH_ALERTS = [
  {
    accountId: "1234",
    accountName: "John Smith IRA",
    currentCash: 1200,
    requiredCash: 2500,
    shortfall: 1300,
  },
  {
    accountId: "5678",
    accountName: "Sarah Johnson 401k",
    currentCash: 500,
    requiredCash: 1500,
    shortfall: 1000,
  },
];

// Add helper function for calculating rebalance alignment score
const calculateRebalanceAlignmentScore = (accountAllocations) => {
  if (!accountAllocations || accountAllocations.length === 0) return 100;

  const deviations = accountAllocations.map((account) =>
    Math.abs(account.currentAllocation - account.targetAllocation),
  );

  const averageDeviation =
    deviations.reduce((sum, dev) => sum + dev, 0) / deviations.length;
  return Math.max(0, Math.min(100, 100 - averageDeviation));
};

// Add helper function for calculating cash utilization
const calculateCashUtilization = (totalCash, totalPortfolioValue) => {
  if (!totalPortfolioValue || totalPortfolioValue === 0) return 0;
  return (totalCash / totalPortfolioValue) * 100;
};

const TradingRebalancing = () => {
  // State management
  const [positions, setPositions] = useState(INITIAL_POSITIONS);
  const [distributions, setDistributions] = useState(INITIAL_DISTRIBUTIONS);
  const [cashAlerts, setCashAlerts] = useState(INITIAL_CASH_ALERTS);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [showPositionsPanel, setShowPositionsPanel] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [tradeForm, setTradeForm] = useState({
    security: "",
    quantity: "",
    type: "Market",
    timeInForce: "Day",
  });

  // Add missing state variables
  const [metrics, setMetrics] = useState({
    pendingTrades: { value: 0, status: "neutral", icon: "⏳" },
    failedTrades: { value: 0, status: "neutral", icon: "❌" },
    smartRecommendations: { value: 0, status: "neutral", icon: "🧠" },
    cashUtilization: { value: "0%", status: "neutral", icon: "💰" },
    rebalanceAlignment: { value: "100%", status: "success", icon: "⚖️" },
    lastExecutionTime: { value: "-", status: "neutral", icon: "🕒" },
    tradeSuccessRate: { value: "100%", status: "success", icon: "📈" },
  });
  const [smartRecommendations, setSmartRecommendations] = useState([]);

  const [rebalanceOptions, setRebalanceOptions] = useState({
    taxAware: true,
    avoidWashSales: true,
    harvestLosses: false,
    maintainCashReserves: true,
    minimumCashPercentage: 5,
  });

  // Add tax loss settings state
  const [taxLossSettings, setTaxLossSettings] = useState({
    enabled: false,
    minimumLoss: 1000,
    holdingPeriod: 30,
    avoidWashSale: true,
  });

  // New state for enhanced functionality
  const [currentStep, setCurrentStep] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filterConfig, setFilterConfig] = useState({
    field: null,
    value: null,
  });
  const [expandedPositionId, setExpandedPositionId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [signature, setSignature] = useState(null);

  // New state for rebalancing
  const [accountAllocations, setAccountAllocations] = useState(
    INITIAL_ACCOUNT_ALLOCATIONS,
  );
  const [firmwideMetrics, setFirmwideMetrics] = useState(
    INITIAL_FIRMWIDE_METRICS,
  );
  const [rebalanceAnalysis, setRebalanceAnalysis] = useState(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [showAllocationChart, setShowAllocationChart] = useState(true);
  const [showSectorChart, setShowSectorChart] = useState(true);

  // Add new state for account filtering
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [orderType, setOrderType] = useState("sell"); // 'buy' or 'sell'
  const [availableCash, setAvailableCash] = useState(0);

  // Memoize the symbols array
  const symbols = useMemo(() => positions.map((p) => p.symbol), [positions]);

  // Initialize market data with memoized symbols
  const {
    marketData,
    isLoading: marketDataLoading,
    lastUpdate,
    refreshMarketData,
  } = useMarketData(symbols);

  // Then initialize trade execution with market data
  const { orderStatus, validationErrors, validateOrder, executeOrder } =
    useTradeExecution({
      selectedAccount,
      accountAllocations,
      marketData,
      setAccountAllocations,
      setMetrics,
    });

  // Initialize iRebal last
  const {
    rebalanceStatus,
    rebalanceResults,
    analyzeRebalancing,
    executeRebalancing,
  } = useIRebal();

  // Memoize the tax loss settings check function
  const checkWashSaleRisk = useCallback((symbol) => {
    // Mock implementation - in real app, would check trading history
    const recentTrades = []; // Would come from API
    return recentTrades.some(
      (trade) =>
        trade.symbol === symbol &&
        trade.type === "buy" &&
        new Date() - new Date(trade.date) <= 30 * 24 * 60 * 60 * 1000,
    );
  }, []); // Empty dependency array since it doesn't depend on any props or state

  // Memoize the tax loss opportunity calculation
  const calculateTaxLossOpportunity = useCallback(
    (position) => {
      if (!position || !position.costBasis) return null;

      const currentValue = position.quantity * position.marketPrice;
      const costBasisValue = position.quantity * position.costBasis;
      const unrealizedGainLoss = currentValue - costBasisValue;
      const percentageChange = (unrealizedGainLoss / costBasisValue) * 100;
      const holdingPeriod = Math.floor(
        (new Date() - new Date(position.acquisitionDate)) /
          (1000 * 60 * 60 * 24),
      );

      return {
        unrealizedGainLoss,
        percentageChange,
        isLongTerm: holdingPeriod >= 365,
        isTaxLossCandidate:
          unrealizedGainLoss < 0 &&
          Math.abs(percentageChange) >= taxLossSettings.minimumLoss &&
          Math.abs(unrealizedGainLoss) >= taxLossSettings.minimumLoss,
        estimatedTaxSavings:
          Math.abs(unrealizedGainLoss) * (holdingPeriod >= 365 ? 0.2 : 0.37),
        washSaleWarning: checkWashSaleRisk(position.symbol),
      };
    },
    [taxLossSettings?.minimumLoss, checkWashSaleRisk],
  );

  // Memoize the smart recommendations generation
  const generateSmartRecommendations = useCallback(() => {
    if (!selectedAccount || !accountAllocations) return [];

    const selectedAccountData = accountAllocations.find(
      (acc) => acc.accountId === selectedAccount,
    );
    if (!selectedAccountData) return [];

    const recommendations = [];

    // Check for tax-loss harvesting opportunities
    selectedAccountData.positions.forEach((position) => {
      const taxLossOpp = calculateTaxLossOpportunity(position);
      if (taxLossOpp?.isTaxLossCandidate) {
        recommendations.push({
          type: "tax-loss-harvest",
          symbol: position.symbol,
          quantity: position.quantity,
          estimatedSavings: taxLossOpp.estimatedTaxSavings,
          unrealizedLoss: taxLossOpp.unrealizedGainLoss,
          action: "sell",
          reason: `Potential tax-loss harvesting opportunity with estimated tax savings of $${taxLossOpp.estimatedTaxSavings.toFixed(2)}`,
        });
      }
    });

    // Check cash position and suggest investments
    if (selectedAccountData.cash > selectedAccountData.totalValue * 0.1) {
      recommendations.push({
        type: "cash-deployment",
        action: "buy",
        estimatedAmount:
          selectedAccountData.cash - selectedAccountData.totalValue * 0.05,
        reason:
          "High cash position detected. Consider deploying excess cash to maintain target allocation.",
      });
    }

    // Check for overweight/underweight positions
    selectedAccountData.portfolioBreakdown.forEach((portfolio) => {
      if (Math.abs(portfolio.allocation - portfolio.targetAllocation) > 5) {
        recommendations.push({
          type: "rebalance",
          portfolio: portfolio.name,
          currentAllocation: portfolio.allocation,
          targetAllocation: portfolio.targetAllocation,
          action:
            portfolio.allocation > portfolio.targetAllocation ? "sell" : "buy",
          reason: `Portfolio ${portfolio.name} is ${portfolio.allocation > portfolio.targetAllocation ? "overweight" : "underweight"} by ${Math.abs(portfolio.allocation - portfolio.targetAllocation).toFixed(1)}%`,
        });
      }
    });

    return recommendations;
  }, [selectedAccount, accountAllocations, calculateTaxLossOpportunity]); // Only depend on stable values

  // Effect to update recommendations
  useEffect(() => {
    if (!taxLossSettings?.enabled) {
      setSmartRecommendations([]);
      return;
    }

    const newRecommendations = generateSmartRecommendations();
    // Remove isEqual check since it causes unnecessary re-renders
    setSmartRecommendations(newRecommendations);
    if (newRecommendations.length > 0) {
      showToastMessage(
        `${newRecommendations.length} new trading opportunities identified`,
        "info",
      );
    }
  }, [taxLossSettings?.enabled, generateSmartRecommendations]); // Remove smartRecommendations from deps

  // Memoize metrics calculation values
  const metricsValues = useMemo(() => {
    if (!accountAllocations || !firmwideMetrics) return null;

    return {
      alignmentScore: calculateRebalanceAlignmentScore(accountAllocations),
      cashUtil: calculateCashUtilization(
        firmwideMetrics.totalCash,
        firmwideMetrics.totalPortfolioValue,
      ),
      recommendationsCount: smartRecommendations.length,
    };
  }, [accountAllocations, firmwideMetrics, smartRecommendations]);

  // Separate effect to update metrics
  useEffect(() => {
    if (!metricsValues) return;

    setMetrics((prev) => {
      // Only update if values have changed
      if (
        prev.smartRecommendations.value ===
          metricsValues.recommendationsCount &&
        parseFloat(prev.cashUtilization.value) === metricsValues.cashUtil &&
        parseFloat(prev.rebalanceAlignment.value) ===
          metricsValues.alignmentScore
      ) {
        return prev;
      }

      return {
        ...prev,
        smartRecommendations: {
          ...prev.smartRecommendations,
          value: metricsValues.recommendationsCount,
          status:
            metricsValues.recommendationsCount > 0 ? "success" : "neutral",
        },
        cashUtilization: {
          ...prev.cashUtilization,
          value: `${metricsValues.cashUtil.toFixed(1)}%`,
          status: metricsValues.cashUtil > 20 ? "warning" : "success",
        },
        rebalanceAlignment: {
          ...prev.rebalanceAlignment,
          value: `${metricsValues.alignmentScore.toFixed(1)}%`,
          status:
            metricsValues.alignmentScore > 95
              ? "success"
              : metricsValues.alignmentScore > 90
                ? "warning"
                : "error",
        },
      };
    });
  }, [metricsValues]); // Only depend on memoized values

  // Workflow step definitions
  const workflowSteps = {
    "individual-trade": [
      {
        title: "Select Account",
        description: "Choose the account for trade execution",
      },
      {
        title: "Trade Details",
        description: "Enter security and trade specifications",
      },
      {
        title: "Review & Validate",
        description: "Review trade details and validate compliance",
      },
      {
        title: "Confirm & Submit",
        description: "Sign and submit the trade order",
      },
    ],
    "model-trades": [
      {
        title: "Select Accounts",
        description: "Choose multiple accounts for trade execution",
      },
      {
        title: "Trade Details",
        description: "Enter trade specifications for each account",
      },
      {
        title: "Review & Validate",
        description: "Review all trades and validate compliance",
      },
      {
        title: "Confirm & Submit",
        description: "Sign and submit trade orders",
      },
    ],
    "raise-cash-rebalance": [
      {
        title: "Cash & Portfolio Goals",
        description: "Set cash needs and target allocations",
      },
      {
        title: "Rebalance Analysis",
        description: "Review proposed trades and tax impact",
      },
      {
        title: "Trade Sequence",
        description: "Optimize trade execution sequence",
      },
      { title: "Confirm & Submit", description: "Sign and submit all orders" },
    ],
    rebalance: [
      {
        title: "Select Accounts",
        description: "Choose accounts to include in rebalancing",
      },
      {
        title: "Current Allocations",
        description: "Review current firmwide allocations",
      },
      {
        title: "Set Targets",
        description: "Adjust target allocations and constraints",
      },
      {
        title: "Review Analysis",
        description: "Review proposed trades and impact",
      },
      {
        title: "Confirm & Submit",
        description: "Sign and submit rebalancing orders",
      },
    ],
  };

  // Enhanced workflow options with metadata
  const workflowOptions = {
    "individual-trade": {
      title: "Process Individual Trade",
      description: "Execute a single trade for one account",
      icon: "👤",
      requiresMarketData: true,
      requiresValidation: true,
    },
    "model-trades": {
      title: "Process Model Trades",
      description: "Execute trades across multiple accounts",
      icon: "👥",
      requiresMarketData: true,
      requiresValidation: true,
    },
    "raise-cash-rebalance": {
      title: "Raise Cash & Rebalance",
      description: "Generate cash while maintaining allocations",
      icon: "⚖️",
      requiresIRebal: true,
      requiresCashAnalysis: true,
    },
    rebalance: {
      title: "Firmwide Rebalancing",
      description: "Rebalance portfolios to target allocations",
      icon: "🎯",
      requiresIRebal: true,
      requiresValidation: true,
    },
    "view-distributions": {
      title: "View Distributions",
      description: "Monitor scheduled distributions and cash",
      icon: "📊",
      requiresCashAnalysis: true,
    },
    "smart-recommendations": {
      title: "Smart Recommendations",
      description: "Generate cash through position sales",
      icon: "🧠",
      requiresCashAnalysis: true,
      requiresValidation: true,
    },
  };

  // Enhanced workflow selection handler
  const handleWorkflowSelect = useCallback(
    (workflowId) => {
      if (hasUnsavedChanges) {
        setShowUnsavedWarning(true);
        setPendingNavigation(() => () => {
          setActiveWorkflow(workflowId);
          setCurrentStep(0);
          setShowPositionsPanel(true);
        });
        return;
      }

      setActiveWorkflow(workflowId);
      setCurrentStep(0);
      setShowPositionsPanel(true);

      // Pre-fetch iRebal analysis if needed
      const workflow = workflowSteps[workflowId];
      if (workflow?.requiresIRebal) {
        analyzeRebalancing(selectedAccounts, rebalanceOptions);
      }
    },
    [selectedAccounts, rebalanceOptions, analyzeRebalancing, hasUnsavedChanges],
  );

  // Step navigation handlers
  const handleStepChange = (stepIndex) => {
    if (stepIndex < currentStep || validateCurrentStep()) {
      setCurrentStep(stepIndex);
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Account Selection
        return selectedAccounts.length > 0;
      case 1: // Trade Details
        return tradeForm.security && tradeForm.quantity;
      case 2: // Review & Validate
        return !validationErrors.length;
      default:
        return true;
    }
  };

  // Enhanced trade submission handler
  const handleTradeSubmit = async (e) => {
    e.preventDefault();

    const order = {
      ...tradeForm,
      orderType,
      accountId: selectedAccount,
      timestamp: new Date().toISOString(),
    };

    const validation = await validateOrder(order);
    if (!validation.isValid) {
      setMetrics((prev) => ({
        ...prev,
        failedTrades: {
          ...prev.failedTrades,
          value: prev.failedTrades.value + 1,
        },
      }));
      showToastMessage("Trade validation failed", "error");
      return;
    }

    setMetrics((prev) => ({
      ...prev,
      pendingTrades: {
        ...prev.pendingTrades,
        value: prev.pendingTrades.value + 1,
      },
    }));

    // Show confirmation modal with validation results
    setConfirmationDetails({
      type: "trade",
      data: order,
      validation,
    });
    setShowConfirmationModal(true);
  };

  // Enhanced final submission handler
  const handleFinalSubmission = async () => {
    if (!signature) {
      showToastMessage("Digital signature required", "error");
      return;
    }

    const result = await executeOrder({
      ...confirmationDetails.data,
      signature,
    });

    if (result.success) {
      setMetrics((prev) => ({
        ...prev,
        pendingTrades: {
          ...prev.pendingTrades,
          value: Math.max(0, prev.pendingTrades.value - 1),
        },
      }));
      showToastMessage(
        `${result.orderType === "buy" ? "Buy" : "Sell"} order executed successfully`,
        "success",
      );
      generateTradeConfirmation(result);
      resetForm();
    } else {
      setMetrics((prev) => ({
        ...prev,
        failedTrades: {
          ...prev.failedTrades,
          value: prev.failedTrades.value + 1,
        },
        pendingTrades: {
          ...prev.pendingTrades,
          value: Math.max(0, prev.pendingTrades.value - 1),
        },
      }));
      showToastMessage("Trade execution failed", "error");
    }

    setShowConfirmationModal(false);
    setSignature(null);
  };

  // Enhanced PDF generation
  const generateTradeConfirmation = (tradeResult) => {
    const doc = new jsPDF();
    const selectedAccountData = accountAllocations.find(
      (acc) => acc.accountId === selectedAccount,
    );

    // Add header
    doc.setFontSize(20);
    doc.text("Trade Confirmation", 20, 20);

    // Add trade details
    doc.setFontSize(12);
    doc.text(`Confirmation Number: ${tradeResult.confirmationNumber}`, 20, 40);
    doc.text(`Account: ${selectedAccountData?.accountName}`, 20, 50);
    doc.text(`Execution Time: ${tradeResult.executionTime}`, 20, 60);
    doc.text(`Order Type: ${tradeResult.orderType.toUpperCase()}`, 20, 70);
    doc.text(`Security: ${tradeForm.security}`, 20, 80);
    doc.text(`Quantity: ${tradeForm.quantity}`, 20, 90);
    doc.text(`Trade Type: ${tradeForm.type}`, 20, 100);
    doc.text(`Time in Force: ${tradeForm.timeInForce}`, 20, 110);
    doc.text(
      `Estimated Value: $${tradeResult.estimatedValue.toFixed(2)}`,
      20,
      120,
    );

    // Add signature
    if (signature) {
      doc.addImage(signature, "PNG", 20, 140, 50, 20);
    }

    // Save the PDF
    doc.save(`trade-confirmation-${tradeResult.confirmationNumber}.pdf`);
  };

  // Toast message handler
  const showToastMessage = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Position table sorting and filtering
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilter = (field, value) => {
    setFilterConfig({ field, value });
  };

  const getSortedAndFilteredPositions = () => {
    let result = [...positions];

    // Apply filters
    if (filterConfig.field && filterConfig.value) {
      result = result.filter((position) => {
        const fieldValue = position[filterConfig.field];
        return String(fieldValue)
          .toLowerCase()
          .includes(String(filterConfig.value).toLowerCase());
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        const direction = sortConfig.direction === "asc" ? 1 : -1;

        if (typeof aValue === "number") {
          return (aValue - bValue) * direction;
        }
        return String(aValue).localeCompare(String(bValue)) * direction;
      });
    }

    return result;
  };

  // Auto-refresh market data
  useEffect(() => {
    let intervalId;
    if (autoRefreshEnabled) {
      intervalId = setInterval(refreshMarketData, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, refreshMarketData]);

  // Form reset helper
  const resetForm = () => {
    setTradeForm({
      security: "",
      quantity: "",
      type: "Market",
      timeInForce: "Day",
    });
    setSelectedAccounts([]);
    setCurrentStep(0);
    setHasUnsavedChanges(false);
  };

  // Render dashboard widgets
  const renderDashboardWidgets = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
      <div data-tooltip-id="pending-trades-tooltip">
        <MetricWidget
          title="Pending Trades"
          value={metrics.pendingTrades.value}
          status={metrics.pendingTrades.status}
          icon={metrics.pendingTrades.icon}
        />
        <Tooltip id="pending-trades-tooltip" place="bottom">
          Number of trade orders waiting for execution
        </Tooltip>
      </div>

      <div data-tooltip-id="failed-trades-tooltip">
        <MetricWidget
          title="Failed Trades"
          value={metrics.failedTrades.value}
          status={metrics.failedTrades.status}
          icon={metrics.failedTrades.icon}
        />
        <Tooltip id="failed-trades-tooltip" place="bottom">
          Number of trades that failed during validation or execution today
        </Tooltip>
      </div>

      <div data-tooltip-id="smart-rec-tooltip">
        <MetricWidget
          title="Smart Recommendations"
          value={metrics.smartRecommendations.value}
          status={metrics.smartRecommendations.status}
          icon={metrics.smartRecommendations.icon}
        />
        <Tooltip id="smart-rec-tooltip" place="bottom">
          Active recommendations for tax-loss harvesting and portfolio
          optimization
        </Tooltip>
      </div>

      <div data-tooltip-id="cash-util-tooltip">
        <MetricWidget
          title="Cash Utilization"
          value={metrics.cashUtilization.value}
          status={metrics.cashUtilization.status}
          icon={metrics.cashUtilization.icon}
        />
        <Tooltip id="cash-util-tooltip" place="bottom">
          Percentage of total portfolio value held in cash
        </Tooltip>
      </div>

      <div data-tooltip-id="rebalance-align-tooltip">
        <MetricWidget
          title="Rebalance Alignment"
          value={metrics.rebalanceAlignment.value}
          status={metrics.rebalanceAlignment.status}
          icon={metrics.rebalanceAlignment.icon}
        />
        <Tooltip id="rebalance-align-tooltip" place="bottom">
          How closely current allocations match target allocations
        </Tooltip>
      </div>
    </div>
  );

  // Render workflow progress
  const renderWorkflowProgress = () => {
    if (!activeWorkflow || !workflowSteps[activeWorkflow]) return null;

    return (
      <WorkflowProgress
        steps={workflowSteps[activeWorkflow]}
        currentStep={currentStep}
        onStepClick={handleStepChange}
      />
    );
  };

  // Enhanced positions panel render
  const renderPositionsPanel = () => {
    const sortedAndFilteredPositions = getSortedAndFilteredPositions();
    const selectedAccountData = accountAllocations.find(
      (acc) => acc.accountId === selectedAccount,
    );

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-none p-6 mt-6 border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Account Positions
          </h3>
          <div className="flex items-center space-x-4">
            {/* Smart Features Buttons */}
            <button
              onClick={() => setShowSmartRecommendationModal(true)}
              className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
              data-tooltip-id="smart-rec-tooltip"
              data-tooltip-content="View smart trade recommendations"
            >
              <span className="mr-2">🧠</span>
              Smart Recommendations
              {smartRecommendations.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                  {smartRecommendations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowTaxLossSettingsModal(true)}
              className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
              data-tooltip-id="tax-loss-tooltip"
              data-tooltip-content="Configure tax-loss harvesting settings"
            >
              <span className="mr-2">⚙️</span>
              Tax-Loss Settings
            </button>

            {/* Account Selection Dropdown */}
            <select
              value={selectedAccount || ""}
              onChange={(e) => {
                const accountId = e.target.value;
                setSelectedAccount(accountId);
                const account = accountAllocations.find(
                  (acc) => acc.accountId === accountId,
                );
                setAvailableCash(account ? account.cash : 0);
              }}
              className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
            >
              <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                Select Account
              </option>
              {accountAllocations.map((account) => (
                <option
                  key={account.accountId}
                  value={account.accountId}
                  className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {account.accountName} - ${account.totalValue.toLocaleString()}
                </option>
              ))}
            </select>

            {/* Buy/Sell Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setOrderType("buy")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    orderType === "buy"
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
              >
                Buy
              </button>
              <button
                onClick={() => setOrderType("sell")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    orderType === "sell"
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
              >
                Sell
              </button>
            </div>

            {lastUpdate && (
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Last update: {new Date(lastUpdate).toLocaleTimeString()}
              </span>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={refreshMarketData}
                disabled={marketDataLoading}
                className={`px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300
                  ${marketDataLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {marketDataLoading ? "Refreshing..." : "Refresh"}
              </button>
              <label className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={autoRefreshEnabled}
                  onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                  className="rounded text-blue-600"
                />
                <span>Auto-refresh</span>
              </label>
            </div>
          </div>
        </div>

        {/* Account Summary */}
              {selectedAccountData && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Available Cash
              </h4>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                ${selectedAccountData.cash.toLocaleString()}
              </p>
            </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                  Total Value
                </h4>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  ${selectedAccountData.totalValue.toLocaleString()}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                  Current Allocation
                </h4>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {selectedAccountData.currentAllocation}%
                </p>
              </div>
            </div>

            {/* Portfolio Breakdown */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-500 mb-2">
                Portfolio Breakdown
              </h4>
              <div className="grid grid-cols-4 gap-4">
                {selectedAccountData.portfolioBreakdown.map(
                  (portfolio, index) => (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm dark:shadow-none border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {portfolio.name}
                        </span>
                        <span className="text-sm font-bold text-blue-500 dark:text-blue-400">
                          {portfolio.allocation}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <div>Risk Score: {portfolio.riskScore}</div>
                        <div>YTD: {portfolio.ytdReturn}%</div>
                        <div>1Y: {portfolio.oneYearReturn}%</div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* Tax-Loss Harvesting Summary */}
            {taxLossSettings.enabled && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                  Tax-Loss Harvesting Opportunities
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-green-600 dark:text-green-400">
                      Available Opportunities
                    </span>
                    <p className="text-lg font-semibold text-green-900 dark:text-green-200">
                      {
                        smartRecommendations.filter(
                          (rec) => rec.type === "tax-loss-harvest",
                        ).length
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-green-600 dark:text-green-400">
                      Potential Tax Savings
                    </span>
                    <p className="text-lg font-semibold text-green-900 dark:text-green-200">
                      $
                      {smartRecommendations
                        .filter((rec) => rec.type === "tax-loss-harvest")
                        .reduce((sum, rec) => sum + rec.estimatedSavings, 0)
                        .toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-green-600 dark:text-green-400">
                      YTD Realized Savings
                    </span>
                    <p className="text-lg font-semibold text-green-900 dark:text-green-200">
                      $1,234.56
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Positions Table */}
        <div className="overflow-x-auto">
          {selectedAccount ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("symbol")}
                    className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                  >
                    Symbol
                    {sortConfig.key === "symbol" && (
                      <span className="ml-1">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    onClick={() => handleSort("name")}
                    className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                  >
                    Name
                    {sortConfig.key === "name" && (
                      <span className="ml-1">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    onClick={() => handleSort("quantity")}
                    className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                  >
                    Quantity
                    {sortConfig.key === "quantity" && (
                      <span className="ml-1">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    onClick={() => handleSort("marketPrice")}
                    className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                  >
                    Market Price
                    {sortConfig.key === "marketPrice" && (
                      <span className="ml-1">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th
                    onClick={() => handleSort("gainLoss")}
                    className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                  >
                    Gain/Loss
                    {sortConfig.key === "gainLoss" && (
                      <span className="ml-1">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {selectedAccountData?.positions.map((position, index) => {
                  // Hardcoded demo data
                  const demoData = {
                    AAPL: { gainLoss: 3750.25, price: 175.50 },
                    MSFT: { gainLoss: -2140.80, price: 325.75 },
                    GOOGL: { gainLoss: 1424.60, price: 135.60 },
                    AMZN: { gainLoss: -890.30, price: 145.25 },
                    META: { gainLoss: 5280.90, price: 325.30 },
                    NVDA: { gainLoss: 4800.75, price: 485.90 }
                  };

                  const demo = demoData[position.symbol] || { gainLoss: 1234.56, price: position.marketPrice || 100.00 };
                  const liveData = marketData[position.symbol];
                  const priceChange = liveData?.priceChange || (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 5).toFixed(2);
                  const priceChangeClass =
                    priceChange > 0
                      ? "text-green-600 dark:text-green-400"
                      : priceChange < 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-gray-600 dark:text-gray-400";
                  const marketPrice = demo.price;
                  const value = marketPrice * (position.quantity || 0);
                  const gainLoss = demo.gainLoss;

                  return (
                    <React.Fragment key={index}>
                      <tr
                        className={`hover:bg-gray-700/50 dark:hover:bg-gray-700/50 cursor-pointer ${expandedPositionId === index ? "bg-gray-50 dark:bg-gray-800" : ""}`}
                        onClick={() =>
                          setExpandedPositionId(
                            expandedPositionId === index ? null : index,
                          )
                        }
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {position.symbol}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                          {position.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                          {position.quantity || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                          <div className="flex items-center justify-end space-x-2">
                            <span>${marketPrice.toFixed(2)}</span>
                            {liveData && (
                              <span className={`text-xs ${priceChangeClass}`}>
                                {priceChange > 0
                                  ? "↑"
                                  : priceChange < 0
                                    ? "↓"
                                    : "–"}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${gainLoss >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                        >
                          ${gainLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                      {expandedPositionId === index && (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                                  Position Details
                                </h4>
                                <dl className="grid grid-cols-2 gap-2 text-sm">
                                  <dt className="text-gray-500 dark:text-gray-400">Cost Basis:</dt>
                                  <dd className="text-gray-900 dark:text-gray-100">
                                    ${position.costBasis?.toFixed(2) || (marketPrice * 0.9).toFixed(2)}
                                  </dd>
                                  <dt className="text-gray-500 dark:text-gray-400">
                                    Acquisition Date:
                                  </dt>
                                  <dd className="text-gray-900 dark:text-gray-100">
                                    {position.acquisitionDate
                                      ? new Date(
                                          position.acquisitionDate,
                                        ).toLocaleDateString()
                                      : "01/01/2023"}
                                  </dd>
                                  <dt className="text-gray-500 dark:text-gray-400">
                                    Unrealized G/L:
                                  </dt>
                                  <dd
                                    className={
                                      gainLoss >= 0
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-red-600 dark:text-red-400"
                                    }
                                  >
                                    ${gainLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </dd>
                                  <dt className="text-gray-500 dark:text-gray-400">
                                    Tax Status:
                                  </dt>
                                  <dd className="text-gray-900 dark:text-gray-100">
                                    Long-term
                                  </dd>
                                </dl>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                                  Quick Actions
                                </h4>
                                <div className="space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOrderType("sell");
                                      setTradeForm((prev) => ({
                                        ...prev,
                                        security: position.symbol,
                                        quantity: position.quantity || 0,
                                      }));
                                    }}
                                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                  >
                                    Sell
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedChartSymbol(position.symbol);
                                      setShowChartModal(true);
                                    }}
                                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                                  >
                                    View Chart
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-500">
              Please select an account to view positions
            </div>
          )}
        </div>
      </div>
    );
  };

  // Toast notification
  const renderToast = () => {
    if (!showToast) return null;

    return (
      <div
        className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg dark:shadow-none ${
          toastType === "success" ? "bg-green-600" : "bg-red-600"
        } text-white`}
      >
        {toastMessage}
      </div>
    );
  };

  // Confirmation modal
  const renderConfirmationModal = () => {
    if (!showConfirmationModal) return null;

    const selectedAccountData = accountAllocations.find(
      (acc) => acc.accountId === selectedAccount,
    );
    const estimatedValue =
      tradeForm.security && tradeForm.quantity
        ? (marketData[tradeForm.security]?.lastPrice || 0) *
          parseFloat(tradeForm.quantity)
        : 0;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-surface-default dark:bg-surface-dark rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl dark:shadow-2xl">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Confirm {orderType === "buy" ? "Buy" : "Sell"} Order
          </h3>

          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Trade Details
                </h4>
                <dl className="mt-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <dt className="text-gray-500 dark:text-gray-400">Account:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      {selectedAccountData?.accountName}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Security:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      {confirmationDetails?.data.security}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Action:</dt>
                    <dd className={`font-medium ${
                      orderType === "buy" 
                        ? "text-green-600 dark:text-green-400" 
                        : "text-red-600 dark:text-red-400"
                    }`}>
                      {orderType.toUpperCase()}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Quantity:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      {confirmationDetails?.data.quantity}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Type:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      {confirmationDetails?.data.type}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Time in Force:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      {confirmationDetails?.data.timeInForce}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Order Summary
                </h4>
                <dl className="mt-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <dt className="text-gray-500 dark:text-gray-400">Market Price:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      $
                      {marketData[tradeForm.security]?.lastPrice.toFixed(2) ||
                        "N/A"}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Estimated Value:</dt>
                    <dd className="text-gray-900 dark:text-gray-100 font-medium">
                      ${estimatedValue.toFixed(2)}
                    </dd>
                    {orderType === "buy" && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">Available Cash:</dt>
                        <dd className="text-gray-900 dark:text-gray-100 font-medium">
                          ${selectedAccountData?.cash.toLocaleString()}
                        </dd>
                        <dt className="text-gray-500 dark:text-gray-400">Remaining Cash:</dt>
                        <dd className={`font-medium ${
                          selectedAccountData?.cash - estimatedValue < 0
                            ? "text-red-600 dark:text-red-400"
                            : "text-gray-900 dark:text-gray-100"
                        }`}>
                          $
                          {(selectedAccountData?.cash - estimatedValue).toFixed(
                            2,
                          )}
                        </dd>
                      </>
                    )}
                  </div>
                </dl>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Digital Signature
              </h4>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
                {signature ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                      Signature captured
                    </span>
                    <button
                      onClick={() => setSignature(null)}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      // In a real implementation, this would open a signature pad
                      setSignature("dummy-signature-data");
                    }}
                    className="w-full py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Click to sign
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={() => setShowConfirmationModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleFinalSubmission}
              disabled={!signature}
              className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors shadow-sm dark:shadow-none
                ${!signature ? "opacity-50 cursor-not-allowed dark:opacity-40" : ""}`}
            >
              Submit Order
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Rebalancing panel render functions
  const renderRebalancingPanel = () => {
    const steps = workflowSteps["rebalance"];
    const step = steps[currentStep];

    return (
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
          {step.title} - {step.description}
        </h3>

        {/* Account Selection - Step 0 */}
        {currentStep === 0 && (
          <div className="mb-8">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Accounts
            </h4>
            <div className="space-y-2">
              {accountAllocations.map((account) => (
                <label
                  key={account.accountId}
                  className="flex items-center space-x-3"
                >
                  <input
                    type="checkbox"
                    checked={selectedAccountIds.includes(account.accountId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAccountIds((prev) => [
                          ...prev,
                          account.accountId,
                        ]);
                      } else {
                        setSelectedAccountIds((prev) =>
                          prev.filter((id) => id !== account.accountId),
                        );
                      }
                    }}
                    className="rounded text-blue-600"
                  />
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {account.accountName}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-500">
                    (Current Allocation: {account.currentAllocation}%)
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Current Allocations - Step 1 */}
        {currentStep === 1 && (
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Portfolio Overview
              </h4>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-500">Total Value</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    ${firmwideMetrics.totalPortfolioValue.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-500">Total Cash</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    ${firmwideMetrics.totalCash.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-500">Cash Allocation</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {firmwideMetrics.cashAllocation.toFixed(2)}%
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-500">Risk Score</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {firmwideMetrics.riskScore}
                  </dd>
                </div>
              </dl>
            </div>

            {showAllocationChart && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Asset Allocation
                  </h4>
                  <button
                    onClick={() => setShowAllocationChart(false)}
                    className="text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  >
                    Hide
                  </button>
                </div>
                <div className="h-48">
                  <Pie
                    data={{
                      labels: Object.keys(firmwideMetrics.assetAllocation),
                      datasets: [
                        {
                          data: Object.values(firmwideMetrics.assetAllocation),
                          backgroundColor: [
                            "rgba(59, 130, 246, 0.8)",
                            "rgba(16, 185, 129, 0.8)",
                            "rgba(251, 191, 36, 0.8)",
                          ],
                        },
                      ],
                    }}
                    options={{
                      plugins: {
                        legend: {
                          position: "right",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Set Targets - Step 2 */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Target Allocations
              </h4>
              <div className="space-y-4">
                {accountAllocations
                  .filter((account) =>
                    selectedAccountIds.includes(account.accountId),
                  )
                  .map((account) => (
                    <div
                      key={account.accountId}
                      className="flex items-center space-x-4"
                    >
                      <span className="text-sm text-gray-900 dark:text-gray-100 w-48">
                        {account.accountName}
                      </span>
                      <input
                        type="number"
                        value={account.targetAllocation}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          setAccountAllocations((prev) =>
                            prev.map((a) =>
                              a.accountId === account.accountId
                                ? { ...a, targetAllocation: newValue }
                                : a,
                            ),
                          );
                        }}
                        className="w-24 px-2 py-1 border border-gray-300 rounded-md"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-500">%</span>
                    </div>
                  ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Rebalancing Options
              </h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rebalanceOptions.taxAware}
                    onChange={(e) =>
                      setRebalanceOptions((prev) => ({
                        ...prev,
                        taxAware: e.target.checked,
                      }))
                    }
                    className="rounded text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Tax-aware rebalancing
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rebalanceOptions.avoidWashSales}
                    onChange={(e) =>
                      setRebalanceOptions((prev) => ({
                        ...prev,
                        avoidWashSales: e.target.checked,
                      }))
                    }
                    className="rounded text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Avoid wash sales
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rebalanceOptions.maintainCashReserves}
                    onChange={(e) =>
                      setRebalanceOptions((prev) => ({
                        ...prev,
                        maintainCashReserves: e.target.checked,
                      }))
                    }
                    className="rounded text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Maintain minimum cash reserves
                  </span>
                </label>
                {rebalanceOptions.maintainCashReserves && (
                  <div className="ml-6 mt-2">
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Minimum Cash Percentage
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={rebalanceOptions.minimumCashPercentage}
                      onChange={(e) =>
                        setRebalanceOptions((prev) => ({
                          ...prev,
                          minimumCashPercentage: parseFloat(e.target.value),
                        }))
                      }
                      className="w-24 px-2 py-1 border border-gray-300 rounded-md"
                    />
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-500">%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Review Analysis - Step 3 */}
        {currentStep === 3 && rebalanceAnalysis && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Rebalancing Analysis
            </h4>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Proposed Trades
                </h5>
                <div className="space-y-2">
                  {rebalanceAnalysis.proposedTrades.map((trade, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{trade.symbol}</span>
                      <span
                        className={
                          trade.action === "BUY"
                            ? "text-green-600"
                            : "text-red-600"
                        }
                      >
                        {trade.action} {trade.quantity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Tax Impact
                </h5>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-gray-500 dark:text-gray-500">Est. Total Gain/Loss:</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    $
                    {rebalanceAnalysis.taxImplications.totalEstimatedGain.toLocaleString()}
                  </dd>
                  <dt className="text-gray-500 dark:text-gray-500">Short-term:</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    $
                    {rebalanceAnalysis.taxImplications.shortTermGains.toLocaleString()}
                  </dd>
                  <dt className="text-gray-500 dark:text-gray-500">Long-term:</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    $
                    {rebalanceAnalysis.taxImplications.longTermGains.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={() => setActiveWorkflow(null)}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
          >
            Cancel
          </button>
          {currentStep === 0 && (
            <button
              onClick={() => {
                if (selectedAccountIds.length === 0) {
                  showToastMessage(
                    "Please select at least one account",
                    "error",
                  );
                  return;
                }
                setCurrentStep(1);
              }}
              disabled={selectedAccountIds.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          )}
          {currentStep === 1 && (
            <button
              onClick={() => setCurrentStep(2)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Set Targets
            </button>
          )}
          {currentStep === 2 && (
            <button
              onClick={async () => {
                const analysis = await analyzeRebalancing(
                  selectedAccountIds,
                  rebalanceOptions,
                );
                if (analysis) {
                  setRebalanceAnalysis(analysis);
                  setCurrentStep(3);
                  showToastMessage("Analysis completed", "success");
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Analyze
            </button>
          )}
          {currentStep === 3 && (
            <button
              onClick={async () => {
                const result = await executeRebalancing(rebalanceAnalysis);
                if (result.success) {
                  showToastMessage(
                    "Rebalancing executed successfully",
                    "success",
                  );
                  // Update positions and metrics with new data
                  // This would come from the API in a real implementation
                  setPositions((prevPositions) => {
                    // Simulate position updates
                    return prevPositions.map((pos) => ({
                      ...pos,
                      quantity: pos.quantity * 1.1, // Dummy update
                    }));
                  });
                  setActiveWorkflow(null);
                } else {
                  showToastMessage("Rebalancing execution failed", "error");
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Execute Rebalancing
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render distributions panel
  const renderDistributions = () => {
    return (
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Recurring Distributions
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Account
                </th>
                <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Next Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
              {distributions.map((dist, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {dist.accountName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                    ${dist.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {dist.frequency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {dist.nextDate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render cash alerts panel
  const renderCashAlerts = () => {
    return (
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Cash Sufficiency Alerts
        </h3>
        <div className="space-y-4">
          {cashAlerts.map((alert, index) => (
            <div
              key={index}
              className="bg-red-50 border border-red-200 rounded-lg p-4"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-red-800 font-medium">
                    {alert.accountName}
                  </h4>
                  <p className="text-red-600 text-sm mt-1">
                    Cash shortfall: ${alert.shortfall.toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    // In a real implementation, this would open a cash resolution workflow
                    showToastMessage(
                      "Cash resolution workflow initiated",
                      "success",
                    );
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Resolve
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Enhanced trade entry form render
  const renderTradeEntryForm = () => {
    if (!selectedAccount) {
      return (
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6">
          <div className="text-center text-gray-500 dark:text-gray-500">
            Please select an account to enter trades
          </div>
        </div>
      );
    }

    const selectedAccountData = accountAllocations.find(
      (acc) => acc.accountId === selectedAccount,
    );
    const estimatedValue =
      tradeForm.security && tradeForm.quantity
        ? (marketData[tradeForm.security]?.lastPrice || 0) *
          parseFloat(tradeForm.quantity)
        : 0;

    // Get tax-loss harvesting info if selling
    const selectedPosition =
      orderType === "sell" && tradeForm.security
        ? selectedAccountData.positions.find(
            (p) => p.symbol === tradeForm.security,
          )
        : null;
    const taxLossOpp = selectedPosition
      ? calculateTaxLossOpportunity(selectedPosition)
      : null;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-none p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Trade Entry</h3>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {orderType === "buy"
                ? `Available Cash: $${selectedAccountData.cash.toLocaleString()}`
                : "Sell Order"}
            </div>
            <button
              onClick={() => setShowSmartRecommendationModal(true)}
              className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <span className="mr-2">🧠</span>
              Smart Recommendations
              {smartRecommendations.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                  {smartRecommendations.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <form onSubmit={handleTradeSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Security
              </label>
              {orderType === "buy" ? (
                <input
                  type="text"
                  value={tradeForm.security}
                  onChange={(e) => {
                    setTradeForm((prev) => ({
                      ...prev,
                      security: e.target.value,
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Enter symbol"
                />
              ) : (
                <select
                  value={tradeForm.security}
                  onChange={(e) => {
                    setTradeForm((prev) => ({
                      ...prev,
                      security: e.target.value,
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                >
                  <option value="" className="text-gray-900 dark:text-gray-100">
                    Select position
                  </option>
                  {selectedAccountData.positions.map((position, index) => {
                    const taxLossOpp = calculateTaxLossOpportunity(position);
                    return (
                      <option
                        key={index}
                        value={position.symbol}
                        className="text-gray-900 dark:text-gray-100"
                      >
                        {position.symbol} ({position.quantity} shares)
                        {taxLossOpp?.isTaxLossCandidate ? " 💰" : ""}
                        {taxLossOpp?.washSaleWarning ? " ⚠️" : ""}
                      </option>
                    );
                  })}
                </select>
              )}
              {marketData[tradeForm.security] && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Bid: ${marketData[tradeForm.security].bidPrice.toFixed(2)} |
                  Ask: ${marketData[tradeForm.security].askPrice.toFixed(2)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quantity
              </label>
              <input
                type="number"
                value={tradeForm.quantity}
                onChange={(e) => {
                  setTradeForm((prev) => ({
                    ...prev,
                    quantity: e.target.value,
                  }));
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                placeholder="Enter quantity"
                min="1"
              />
              {estimatedValue > 0 && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Estimated Value: ${estimatedValue.toFixed(2)}
                  {orderType === "buy" &&
                    estimatedValue > selectedAccountData.cash && (
                      <span className="text-red-500 ml-2">
                        Insufficient funds
                      </span>
                    )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trade Type
              </label>
              <select
                value={tradeForm.type}
                onChange={(e) => {
                  setTradeForm((prev) => ({ ...prev, type: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
              >
                <option value="Market" className="text-gray-900 dark:text-gray-100">Market</option>
                <option value="Limit" className="text-gray-900 dark:text-gray-100">Limit</option>
                <option value="Stop" className="text-gray-900 dark:text-gray-100">Stop</option>
                <option value="Stop Limit" className="text-gray-900 dark:text-gray-100">Stop Limit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time in Force
              </label>
              <select
                value={tradeForm.timeInForce}
                onChange={(e) => {
                  setTradeForm((prev) => ({
                    ...prev,
                    timeInForce: e.target.value,
                  }));
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
              >
                <option value="Day" className="text-gray-900 dark:text-gray-100">Day</option>
                <option value="GTC" className="text-gray-900 dark:text-gray-100">GTC</option>
                <option value="FOK" className="text-gray-900 dark:text-gray-100">FOK</option>
                <option value="IOC" className="text-gray-900 dark:text-gray-100">IOC</option>
              </select>
            </div>
          </div>

          {/* Tax-Loss Harvesting Information */}
          {orderType === "sell" && taxLossOpp && (
            <div
              className={`p-4 rounded-lg ${
                taxLossOpp.isTaxLossCandidate
                  ? "bg-green-50 border border-green-200"
                  : taxLossOpp.washSaleWarning
                    ? "bg-yellow-50 border border-yellow-200"
                    : "bg-gray-50 border border-gray-200 dark:border-gray-700"
              }`}
            >
              <h4
                className={`text-sm font-medium mb-2 ${
                  taxLossOpp.isTaxLossCandidate
                    ? "text-green-800"
                    : taxLossOpp.washSaleWarning
                      ? "text-yellow-800"
                      : "text-gray-800 dark:text-gray-200"
                }`}
              >
                Tax Impact Analysis
              </h4>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 dark:text-gray-500">Cost Basis</dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    $
                    {(
                      selectedPosition.costBasis * selectedPosition.quantity
                    ).toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-500">Estimated Proceeds</dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    ${estimatedValue.toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-500">Unrealized Gain/Loss</dt>
                  <dd
                    className={`font-medium ${
                      taxLossOpp.unrealizedGainLoss >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    ${taxLossOpp.unrealizedGainLoss.toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-500">Holding Period</dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    {taxLossOpp.isLongTerm ? "Long-term" : "Short-term"}
                  </dd>
                </div>
                {taxLossOpp.isTaxLossCandidate && (
                  <div className="col-span-2">
                    <dt className="text-green-600 font-medium">
                      Estimated Tax Savings
                    </dt>
                    <dd className="text-green-700 text-lg font-semibold">
                      ${taxLossOpp.estimatedTaxSavings.toFixed(2)}
                    </dd>
                  </div>
                )}
                {taxLossOpp.washSaleWarning && (
                  <div className="col-span-2">
                    <p className="text-yellow-700">
                      ⚠️ Warning: This trade may be subject to wash sale rules.
                      Similar security was purchased within the last 30 days.
                    </p>
                  </div>
                )}
              </dl>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-sm font-medium text-red-800 mb-2">
                Validation Errors:
              </h4>
              <ul className="list-disc list-inside text-sm text-red-700">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                orderStatus === "validating" ||
                orderStatus === "executing" ||
                !tradeForm.security ||
                !tradeForm.quantity ||
                (orderType === "buy" &&
                  estimatedValue > selectedAccountData.cash)
              }
              className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700
                ${
                  orderStatus === "validating" ||
                  orderStatus === "executing" ||
                  !tradeForm.security ||
                  !tradeForm.quantity ||
                  (orderType === "buy" &&
                    estimatedValue > selectedAccountData.cash)
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
            >
              {orderStatus === "validating"
                ? "Validating..."
                : orderStatus === "executing"
                  ? "Executing..."
                  : "Preview Order"}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    {
      label: "Trading & Rebalancing",
      link: "/portfolio-management/trading-rebalancing",
    },
  ];

  // Add new state for chart modal
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedChartSymbol, setSelectedChartSymbol] = useState(null);

  // Add new state for chart data
  const [chartData, setChartData] = useState(null);

  // Add this useEffect at the component level, before the render functions
  useEffect(() => {
    if (showChartModal && selectedChartSymbol) {
      const selectedAccountData = accountAllocations.find(
        (acc) => acc.accountId === selectedAccount,
      );
      const position = selectedAccountData?.positions.find(
        (p) => p.symbol === selectedChartSymbol,
      );

      if (position) {
        const basePrice = position.marketPrice;
        const minPrice = basePrice * 0.95; // 5% below base price
        const maxPrice = basePrice * 1.05; // 5% above base price

        // Generate more realistic price data
        const generatePriceData = () => {
          let currentPrice = basePrice;
          return Array(7)
            .fill(0)
            .map(() => {
              // Add small random change (-0.5% to +0.5% from previous price)
              const change = currentPrice * (Math.random() * 0.01 - 0.005);
              currentPrice += change;
              // Keep price within bounds
              currentPrice = Math.max(
                minPrice,
                Math.min(maxPrice, currentPrice),
              );
              return currentPrice;
            });
        };

        const newChartData = {
          labels: ["1D", "5D", "1M", "3M", "6M", "YTD", "1Y"]
            .map((label, index) => {
              const date = new Date();
              date.setDate(date.getDate() - index * 5);
              return date.toLocaleDateString();
            })
            .reverse(),
          datasets: [
            {
              label: selectedChartSymbol,
              data: generatePriceData().reverse(),
              borderColor: "rgb(59, 130, 246)",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              tension: 0.4,
            },
          ],
        };

        setChartData(newChartData);
      }
    }
  }, [
    showChartModal,
    selectedChartSymbol,
    selectedAccount,
    accountAllocations,
  ]);

  // Update the renderChartModal function
  const renderChartModal = () => {
    if (!showChartModal || !selectedChartSymbol) return null;

    const selectedAccountData = accountAllocations.find(
      (acc) => acc.accountId === selectedAccount,
    );
    const position = selectedAccountData?.positions.find(
      (p) => p.symbol === selectedChartSymbol,
    );
    if (!position) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-surface-default dark:bg-surface-dark rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl dark:shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {selectedChartSymbol} Price History
            </h3>
            <button
              onClick={() => {
                setShowChartModal(false);
                setChartData(null); // Clear chart data when closing
              }}
              className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
            >
              ✕
            </button>
          </div>
          <div className="h-96 bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            {chartData && (
              <Line
                data={{
                  ...chartData,
                  datasets: chartData.datasets.map(dataset => ({
                    ...dataset,
                    borderColor: isDarkMode ? '#60A5FA' : '#2563EB',
                    backgroundColor: isDarkMode ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.1)',
                    pointBackgroundColor: isDarkMode ? '#60A5FA' : '#2563EB',
                    pointHoverBackgroundColor: isDarkMode ? '#93C5FD' : '#3B82F6',
                  }))
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: { duration: 0 },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      mode: "index",
                      intersect: false,
                      backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                      titleColor: isDarkMode ? '#fff' : '#111827',
                      bodyColor: isDarkMode ? '#fff' : '#111827',
                      borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
                      borderWidth: 1,
                      padding: 10,
                    },
                  },
                  scales: {
                    x: {
                      grid: {
                        color: isDarkMode ? 'rgba(75, 85, 99, 0.1)' : 'rgba(209, 213, 219, 0.2)',
                        borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.3)',
                      },
                      ticks: {
                        color: isDarkMode ? '#9CA3AF' : '#4B5563',
                      }
                    },
                    y: {
                      min: position.marketPrice * 0.95,
                      max: position.marketPrice * 1.05,
                      grid: {
                        color: isDarkMode ? 'rgba(75, 85, 99, 0.1)' : 'rgba(209, 213, 219, 0.2)',
                        borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.3)',
                      },
                      ticks: {
                        color: isDarkMode ? '#9CA3AF' : '#4B5563',
                        callback: (value) => `$${value.toFixed(2)}`,
                      },
                    },
                  },
                  interaction: {
                    mode: "nearest",
                    axis: "x",
                    intersect: false,
                  },
                }}
              />
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Position Summary
              </h4>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-gray-500 dark:text-gray-400">Current Price:</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-medium">
                  ${position.marketPrice.toFixed(2)}
                </dd>
                <dt className="text-gray-500 dark:text-gray-400">Quantity:</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-medium">{position.quantity}</dd>
                <dt className="text-gray-500 dark:text-gray-400">Market Value:</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-medium">
                  ${(position.marketPrice * position.quantity).toFixed(2)}
                </dd>
              </dl>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Performance
              </h4>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-gray-500 dark:text-gray-400">Day Change:</dt>
                <dd className="text-green-600 dark:text-green-400 font-medium">+1.25%</dd>
                <dt className="text-gray-500 dark:text-gray-400">Week Change:</dt>
                <dd className="text-red-600 dark:text-red-400 font-medium">-0.75%</dd>
                <dt className="text-gray-500 dark:text-gray-400">Month Change:</dt>
                <dd className="text-green-600 dark:text-green-400 font-medium">+3.50%</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add new state for tax-loss harvesting and smart features
  const [showSmartRecommendationModal, setShowSmartRecommendationModal] =
    useState(false);
  const [showTaxLossSettingsModal, setShowTaxLossSettingsModal] =
    useState(false);
  const [showHelpTour, setShowHelpTour] = useState(false);

  // Add cost basis to positions for tax-loss harvesting calculations
  const [positionsWithCostBasis, setPositionsWithCostBasis] = useState(
    INITIAL_POSITIONS.map((pos) => ({
      ...pos,
      costBasis: pos.marketPrice * (1 + (Math.random() * 0.2 - 0.1)), // Random cost basis ±10% of current price
      acquisitionDate: new Date(
        Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000,
      ), // Random date within last year
    })),
  );

  // Add tax-loss settings modal render function
  const renderTaxLossSettingsModal = () => {
    if (!showTaxLossSettingsModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-surface-default dark:bg-surface-dark rounded-lg p-6 max-w-md w-full mx-4 shadow-xl dark:shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Tax-Loss Harvesting Settings
            </h3>
            <button
              onClick={() => setShowTaxLossSettingsModal(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={taxLossSettings.enabled}
                  onChange={(e) =>
                    setTaxLossSettings((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                  className="rounded text-blue-600 dark:text-blue-500 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                  Enable Tax-Loss Harvesting
                </span>
              </label>
            </div>

            {taxLossSettings.enabled && (
              <>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Minimum Loss Threshold ($)
                  </label>
                  <input
                    type="number"
                    value={taxLossSettings.minimumLoss}
                    onChange={(e) =>
                      setTaxLossSettings((prev) => ({
                        ...prev,
                        minimumLoss: parseFloat(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    min="0"
                  />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Holding Period (days)
                  </label>
                  <input
                    type="number"
                    value={taxLossSettings.holdingPeriod}
                    onChange={(e) =>
                      setTaxLossSettings((prev) => ({
                        ...prev,
                        holdingPeriod: parseInt(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    min="0"
                  />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={taxLossSettings.avoidWashSale}
                      onChange={(e) =>
                        setTaxLossSettings((prev) => ({
                          ...prev,
                          avoidWashSale: e.target.checked,
                        }))
                      }
                      className="rounded text-blue-600 dark:text-blue-500 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                      Avoid Wash Sales
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={() => setShowTaxLossSettingsModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowTaxLossSettingsModal(false);
                showToastMessage("Tax-loss harvesting settings saved", "success");
              }}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors shadow-sm dark:shadow-none"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add smart recommendations modal render function
  const renderSmartRecommendationsModal = () => {
    if (!showSmartRecommendationModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-surface-default dark:bg-surface-dark rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl dark:shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Smart Trading Recommendations
            </h3>
            <button
              onClick={() => setShowSmartRecommendationModal(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              ✕
            </button>
          </div>

          {smartRecommendations.length > 0 ? (
            <div className="space-y-4">
              {smartRecommendations.map((rec, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    rec.type === "tax-loss-harvest"
                      ? "bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700"
                      : rec.type === "cash-deployment"
                        ? "bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700"
                        : "bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4
                        className={`text-sm font-medium mb-1 ${
                          rec.type === "tax-loss-harvest"
                            ? "text-green-800 dark:text-green-300"
                            : rec.type === "cash-deployment"
                              ? "text-blue-800 dark:text-blue-300"
                              : "text-yellow-800 dark:text-yellow-300"
                        }`}
                      >
                        {rec.type === "tax-loss-harvest"
                          ? "Tax-Loss Harvesting Opportunity"
                          : rec.type === "cash-deployment"
                            ? "Cash Deployment Opportunity"
                            : "Rebalancing Opportunity"}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{rec.reason}</p>
                    </div>
                    <button
                      onClick={() => {
                        if (
                          rec.type === "tax-loss-harvest" ||
                          rec.type === "rebalance"
                        ) {
                          setOrderType(rec.action);
                          setTradeForm((prev) => ({
                            ...prev,
                            security: rec.symbol,
                            quantity: rec.quantity,
                          }));
                        }
                        setShowSmartRecommendationModal(false);
                        showToastMessage(
                          "Trade prepared based on recommendation",
                          "success",
                        );
                      }}
                      className={`px-4 py-2 rounded text-white ${
                        rec.type === "tax-loss-harvest"
                          ? "bg-green-600 hover:bg-green-700"
                          : rec.type === "cash-deployment"
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-yellow-600 hover:bg-yellow-700"
                      }`}
                    >
                      Apply Recommendation
                    </button>
                  </div>

                  {rec.type === "tax-loss-harvest" && (
                    <dl className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Security</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100">
                          {rec.symbol}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Quantity</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100">
                          {rec.quantity}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Unrealized Loss</dt>
                        <dd className="font-medium text-red-600">
                          ${Math.abs(rec.unrealizedLoss).toFixed(2)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Est. Tax Savings</dt>
                        <dd className="font-medium text-green-600">
                          ${rec.estimatedSavings.toFixed(2)}
                        </dd>
                      </div>
                    </dl>
                  )}

                  {rec.type === "cash-deployment" && (
                    <dl className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Available Cash</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100">
                          ${rec.estimatedAmount.toFixed(2)}
                        </dd>
                      </div>
                    </dl>
                  )}

                  {rec.type === "rebalance" && (
                    <dl className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Portfolio</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100">
                          {rec.portfolio}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Current Allocation</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100">
                          {rec.currentAllocation}%
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-500">Target Allocation</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100">
                          {rec.targetAllocation}%
                        </dd>
                      </div>
                    </dl>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-500">
              No recommendations available at this time
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setShowSmartRecommendationModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  

  return (
    <SubPageLayout
      title="Trading & Rebalancing"
      subtitle="Manage trades and portfolio rebalancing activities"
      breadcrumbItems={breadcrumbItems}
    >
      {/* Dashboard Widgets */}
      {renderDashboardWidgets()}

      {/* Main Content */}
      <div className="space-y-6">
        {/* Workflow Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(workflowOptions).map(([id, workflow]) => (
            <button
              key={id}
              onClick={() => handleWorkflowSelect(id)}
              className={`p-6 bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none hover:shadow-xl transition-shadow text-left
                ${activeWorkflow === id ? "ring-2 ring-blue-500" : ""}`}
            >
              <div className="flex items-start space-x-4">
                <span className="text-2xl">{workflow.icon}</span>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {workflow.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {workflow.description}
                  </p>
                  {workflow.requiresIRebal &&
                    rebalanceStatus === "analyzing" && (
                      <p className="mt-2 text-xs text-blue-600">
                        Analyzing portfolio...
                      </p>
                    )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Dynamic Content Based on Workflow */}
        {showPositionsPanel && renderPositionsPanel()}
        {activeWorkflow === "view-distributions" && (
          <>
            {renderRebalancingPanel()}
            {renderDistributions()}
            {renderCashAlerts()}
          </>
        )}
        {(activeWorkflow === "individual-trade" ||
          activeWorkflow === "model-trades" ||
          activeWorkflow === "raise-cash-rebalance") &&
          renderTradeEntryForm()}
      </div>

      {/* Modals and Notifications */}
      {renderConfirmationModal()}
      {renderChartModal()}
      {renderToast()}
      <UnsavedChangesWarning
        isVisible={showUnsavedWarning}
        onConfirm={() => {
          setShowUnsavedWarning(false);
          setHasUnsavedChanges(false);
          if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
          }
        }}
        onCancel={() => {
          setShowUnsavedWarning(false);
          setPendingNavigation(null);
        }}
      />
      {renderTaxLossSettingsModal()}
      {renderSmartRecommendationsModal()}
    </SubPageLayout>
  );
};

export default TradingRebalancing;
